import React, { Component, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import "./CertificateVerifyPage.css";

const API = API_BASE_URL;

function formatDisplayDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const parsed = new Date(raw.includes("T") ? raw : `${raw}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function CertificateVerifyView() {
  const params = useParams();
  const certificateId = useMemo(() => String(params.certificateId || "").trim(), [params.certificateId]);
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!certificateId) {
      setLoading(false);
      setInvalid(true);
      return;
    }

    let cancelled = false;

    async function loadVerification() {
      setLoading(true);
      setInvalid(false);
      setError(false);
      setCertificate(null);
      try {
        const res = await fetch(`${API}/api/certificates/verify/${encodeURIComponent(certificateId)}`, {
          cache: "no-store",
        });
        const payload = await res.json().catch(() => null);
        if (!cancelled) {
          if (!res.ok) {
            setError(Number(res.status) >= 500 || Number(res.status) === 0);
            setInvalid(Number(res.status) === 404);
          } else if (!payload?.valid) {
            setInvalid(true);
          } else {
            setCertificate(payload);
          }
        }
      } catch (_error) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadVerification();
    return () => {
      cancelled = true;
    };
  }, [certificateId]);

  return (
    <main className="cv-page">
      <section className="cv-shell" aria-label="Certificate verification">
        <div className="cv-badge">HIRI Verification</div>
        <h1 className={`cv-title ${invalid ? "is-invalid" : ""}`}>
          {loading
            ? "Verifying certificate"
            : error
              ? "Certificate verification is temporarily unavailable."
              : invalid
              ? "Certificate could not be verified."
              : "Certificate Verified"}
        </h1>
        <p className="cv-subtitle">
          {loading
            ? "Checking the certificate record."
            : error
              ? "Please try again later."
              : invalid
              ? "This certificate record could not be found."
              : "The certificate details below match the public record."}
        </p>

        {!loading && !error && !invalid && certificate ? (
          <dl className="cv-grid">
            <div>
              <dt>Student</dt>
              <dd>{certificate.student_name}</dd>
            </div>
            <div>
              <dt>Programme</dt>
              <dd>
                <a href="https://hiri.uk/course" target="_blank" rel="noopener noreferrer">
                  {certificate.programme_name}
                </a>
              </dd>
            </div>
            <div>
              <dt>Completion Date</dt>
              <dd>{formatDisplayDate(certificate.completion_date)}</dd>
            </div>
            <div>
              <dt>Instructor</dt>
              <dd>
                {certificate.instructor}
                {certificate.instructor === "Shaanzae Shahid" ? (
                  <a
                    className="cv-profile-link"
                    href="https://www.linkedin.com/in/shaanza%C3%A9-shahid-b82418a2/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View LinkedIn profile <span aria-hidden="true">↗</span>
                  </a>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>Issued by</dt>
              <dd>
                <a href="https://hiri.uk" target="_blank" rel="noopener noreferrer">
                  {certificate.issuer}
                </a>
              </dd>
            </div>
            <div>
              <dt>Certificate ID</dt>
              <dd>{certificate.certificate_id}</dd>
            </div>
            <div>
              <dt>Certificate Type</dt>
              <dd>{certificate.certificate_type}</dd>
            </div>
          </dl>
        ) : null}

        {loading ? <div className="cv-loading" aria-hidden="true" /> : null}
      </section>
    </main>
  );
}

class CertificateVerifyErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="cv-page">
          <section className="cv-shell" aria-label="Certificate verification">
            <div className="cv-badge">HIRI Verification</div>
            <h1 className="cv-title is-invalid">Certificate verification is temporarily unavailable.</h1>
            <p className="cv-subtitle">
              Please try again later.
            </p>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

export default function CertificateVerifyPage() {
  return (
    <CertificateVerifyErrorBoundary>
      <CertificateVerifyView />
    </CertificateVerifyErrorBoundary>
  );
}
